import { Comment } from '../../types/comment';
import { formatDateTime } from '../../utils/dateUtils';
import { formatProfilePictureUrl } from '../../utils/imageUtils';
import { useTheme } from '../../contexts/ThemeContext';
import { TrashIcon, PencilIcon, CheckIcon, XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AttachmentPreview from './AttachmentPreview';
import FileUpload from '../ui/custom/FileUpload';
import { uploadService } from '../../services/uploadService';
import { id } from 'date-fns/locale';
import FilePreview from './FilePreview';


// Type for upload progress callback
type UploadProgressCallback = (progress: { loaded: number; total: number }, fileName: string) => void;

interface TicketCommentListProps {
  comments: Comment[];
  loggedInUserId: number; // Pass the logged-in user's ID as a prop
  onDeleteComment?: (commentId: number) => Promise<void>;
  onEditComment?: (commentId: number, content: string, attachments?: File[], onProgress?: UploadProgressCallback) => Promise<void>;
  onDeleteAttachment?: (attachmentId: number) => Promise<void>;
  ticketId?: number | string; // Add ticketId to handle file uploads
}

function getColorClass(initial: string): string {
  const colorMap: { [key: string]: string } = {
    A: 'bg-red-500',
    B: 'bg-blue-500',
    C: 'bg-green-500',
    D: 'bg-yellow-500',
    E: 'bg-purple-500',
    F: 'bg-indigo-500',
    G: 'bg-teal-500',
    H: 'bg-pink-500',
    I: 'bg-orange-500',
    J: 'bg-cyan-500',
    K: 'bg-lime-500',
    L: 'bg-emerald-500',
    M: 'bg-rose-500',
    N: 'bg-violet-500',
    O: 'bg-amber-500',
    P: 'bg-fuchsia-500',
    Q: 'bg-gray-500',
    R: 'bg-red-400',
    S: 'bg-blue-400',
    T: 'bg-green-400',
    U: 'bg-yellow-400',
    V: 'bg-purple-400',
    W: 'bg-indigo-400',
    X: 'bg-teal-400',
    Y: 'bg-pink-400',
    Z: 'bg-orange-400',
    '?': 'bg-gray-400',
  };

  return colorMap[initial] || 'bg-gray-500';
}

const getRoleBadgeClasses = (role: string, isDark: boolean): string => {
  const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
  
  if (isDark) {
    switch (role) {
      case 'admin':
        return `${baseClasses} bg-purple-700 text-gray-300`;
      case 'agent':
        return `${baseClasses} bg-green-700 text-gray-300`;
      case 'requester':
        return `${baseClasses} bg-blue-700 text-gray-300`;
      default:
        return `${baseClasses} bg-gray-700/60 text-gray-300`;
    }
  } else {
    switch (role) {
      case 'admin':
        return `${baseClasses} bg-purple-300 text-gray-600`;
      case 'agent':
        return `${baseClasses} bg-green-300 text-gray-600`;
      case 'requester':
        return `${baseClasses} bg-blue-300 text-gray-600`;
      default:
        return `${baseClasses} bg-gray-300 text-gray-600`;
    }
  }
};

export default function TicketCommentList({
  comments,
  loggedInUserId,
  onDeleteComment,
  onEditComment,
  onDeleteAttachment,
}: TicketCommentListProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [editAttachments, setEditAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isUploading, setIsUploading] = useState(false);

  const handleDelete = async (commentId: number) => {
    try {
      await onDeleteComment?.(commentId);
      toast.success(t('tickets.commentDeletedSuccess', 'Comment deleted successfully'));
    } catch (error) {
      toast.error(t('tickets.commentDeleteFailed', 'Failed to delete comment'));
      console.error('Error deleting comment:', error);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await onDeleteAttachment?.(attachmentId);
      toast.success(t('tickets.attachmentDeletedSuccess', 'Attachment deleted successfully'));
    } catch (error) {
      toast.error(t('tickets.attachmentDeleteFailed', 'Failed to delete attachment'));
      console.error('Error deleting attachment:', error);
    }
  };

  const handleStartEdit = (commentId: number, content: string) => {
    setEditingCommentId(commentId);
    setEditContent(content);
    setEditAttachments([]);
    setUploadProgress({});
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent('');
    setEditAttachments([]);
    setUploadProgress({});
  };

  const handleSaveEdit = async (commentId: number) => {
    if (!editContent.trim() && (!editAttachments || editAttachments.length === 0)) {
      toast.error(t('tickets.commentOrAttachmentRequired', 'Comment or attachment is required'));
      return;
    }

    try {
      setIsUploading(true);
      // Create a progress callback to update the upload progress
      const onUploadProgress = (progressEvent: { loaded: number; total: number }, fileName: string) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({
          ...prev,
          [fileName]: percentCompleted
        }));
      };

      // Call the edit comment handler with the progress callback
      await onEditComment?.(commentId, editContent, editAttachments, onUploadProgress);
      
      // Reset state after successful upload
      setEditingCommentId(null);
      setEditContent('');
      setEditAttachments([]);
      setUploadProgress({});
      setIsUploading(false);
    } catch (error) {
      toast.error(t('tickets.commentUpdateFailed', 'Failed to update comment'));
      console.error('Error updating comment:', error);
    }
  };

  const handleFileUpload = (files: File[]) => {
    setEditAttachments(files);
    // Initialize progress for new files
    const newProgress = { ...uploadProgress };
    files.forEach(file => {
      if (!(file.name in newProgress)) {
        newProgress[file.name] = 0;
      }
    });
    setUploadProgress(newProgress);
  };

  // Update progress for file uploads - this will be called by the parent component
  // when files are being uploaded

  // Format date in Myanmar timezone (UTC+06:30)
  const formatDateInMyanmarTz = (dateStr: string) => {
    return formatDateTime(dateStr, { timeZone: 'Asia/Yangon' });
  };

  return (
    <div className="space-y-6">
      {comments.map((comment) => {
        const isLoggedInUser = comment.user.id === loggedInUserId;
        const isEditing = editingCommentId === comment.id;

        return (
          <div
            key={comment.id}
            className={`flex ${isLoggedInUser ? 'flex-row-reverse' : 'flex-row'} items-start space-x-4 ${isLoggedInUser ? 'space-x-reverse' : ''}`}
          >
            {/* Avatar */}
            <div className="flex-shrink-0">
            <div
              className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full relative overflow-hidden border-2 ${
                isLoggedInUser 
                  ? theme === 'dark' ? 'border-blue-400/30' : 'border-blue-300/50'
                  : theme === 'dark' ? 'border-gray-600/30' : 'border-gray-300/50'
              } ${comment.user.pfp_url ? '' : `flex items-center justify-center text-white font-semibold text-xs sm:text-sm ${getColorClass(
                comment.user.full_name?.[0]?.toUpperCase() || '?'
              )} shadow-lg`}`}
            >
                {comment.user.pfp_url ? (
                  <img 
                    src={formatProfilePictureUrl(comment.user.pfp_url)}
                    alt={comment.user.full_name || 'User'}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      const imgElement = e.target as HTMLImageElement;
                      imgElement.style.display = 'none';
                      const nextElement = imgElement.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div 
                  className={`${comment.user.pfp_url ? 'hidden' : 'flex'} h-full w-full items-center justify-center text-white font-semibold text-xs sm:text-sm ${getColorClass(
                    comment.user.full_name?.[0]?.toUpperCase() || '?'
                  )}`}
                >
                  {comment.user.full_name?.[0]?.toUpperCase() || '?'}
                </div>
              </div>
            </div>

            {/* Comment Bubble */}
            <div
              className={`relative w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg px-3 sm:px-4 md:px-5 py-3 sm:py-4 rounded-2xl shadow-lg backdrop-blur-sm border transition-all duration-200 hover:shadow-xl ${
                isLoggedInUser
                  ? theme === 'dark'
                    ? 'bg-blue-900/60 text-blue-50 border-blue-800/40 shadow-blue-900/20'
                    : 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-900 border-blue-200/60 shadow-blue-200/30'
                  : theme === 'dark'
                  ? 'bg-gray-700/80 text-gray-100 border-gray-700/40 shadow-gray-900/30'
                  : 'bg-gray-200 text-gray-900 border-gray-200/60 shadow-gray-200/20'
              } ${isLoggedInUser ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} group`}
            >
              {/* Tail for speech bubble effect */}
              <div
                className={`absolute top-4 w-3 h-3 transform rotate-45 ${
                  isLoggedInUser
                    ? theme === 'dark'
                      ? 'bg-blue-900/60 border-r border-b border-blue-800/40 -right-1.5'
                      : 'bg-gradient-to-br from-blue-50 to-blue-100 border-r border-b border-blue-200/60 -right-1.5'
                    : theme === 'dark'
                    ? 'bg-gray-700/80 border-r border-b border-gray-700/40 -left-1.5'
                    : 'bg-gray-200 border-r border-b border-gray-200/60 -left-1.5'
                }`}
              />

              {/* Action Buttons - Only shown for logged in user's comments when not editing */}
              {isLoggedInUser && !isEditing && (
                <div className="absolute -right-2 sm:-right-3 -top-2 sm:-top-3 flex opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:scale-105">
                  {/* Edit Button */}
                  {onEditComment && (
                    <button
                      onClick={() => handleStartEdit(comment.id, comment.content)}
                      className={`p-1.5 sm:p-2 rounded-full mr-1 sm:mr-2 transition-all duration-200 transform hover:scale-110 ${
                        theme === 'dark' 
                          ? 'bg-gray-700/90 hover:bg-gray-600 border border-gray-600/50 shadow-lg shadow-gray-900/30' 
                          : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-lg shadow-gray-200/40'
                      }`}
                      title={t('tickets.editComment', 'Edit comment')}
                    >
                      <PencilIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                    </button>
                  )}
                  {/* Delete Button */}
                  {onDeleteComment && (
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 transform hover:scale-110 ${
                        theme === 'dark' 
                          ? 'bg-gray-700/90 hover:bg-red-900/50 border border-gray-600/50 hover:border-red-800/50 shadow-lg shadow-gray-900/30' 
                          : 'bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 shadow-lg shadow-gray-200/40'
                      }`}
                      title={t('tickets.deleteComment', 'Delete comment')}
                    >
                      <TrashIcon className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 hover:text-red-600" />
                    </button>
                  )}
                </div>
              )}

              {/* Comment Content - Either editable textarea or static text */}
              {isEditing ? (
                <div className="w-full group/edit">
                  {/* Inline edit textarea */}
                  <div className="relative">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className={`w-full p-0 bg-transparent border-none text-sm leading-relaxed resize-none outline-none focus:ring-0 placeholder-opacity-60 ${
                        theme === 'dark'
                          ? 'text-gray-100 placeholder-gray-400'
                          : 'text-gray-900 placeholder-gray-500'
                      }`}
                      style={{ 
                        minHeight: 'auto',
                        fontFamily: 'inherit',
                        lineHeight: 'inherit'
                      }}
                      rows={Math.max(2, editContent.split('\n').length)}
                      placeholder="Edit your comment..."
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          handleSaveEdit(comment.id);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                    />
                    
                    {/* Subtle edit indicator */}
                    <div className={`absolute -left-2 top-0 w-0.5 h-full rounded-full ${
                      theme === 'dark' ? 'bg-blue-400/40' : 'bg-blue-500/40'
                    }`}></div>
                  </div>
                  
                  {/* Compact action buttons */}
                  <div className={`flex items-center justify-between mt-2 pt-2 border-t ${
                    theme === 'dark' ? 'border-gray-700/30' : 'border-gray-200/30'
                  }`}>
                    <div className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      <kbd className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                        theme === 'dark' ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
                      </kbd>
                      <span className="ml-1">to save •</span>
                      <kbd className={`ml-1 px-1.5 py-0.5 rounded text-xs font-mono ${
                        theme === 'dark' ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        Esc
                      </kbd>
                      <span className="ml-1">to cancel</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleCancelEdit}
                        className={`p-1.5 rounded-lg transition-all duration-200 hover:scale-110 ${
                          theme === 'dark' 
                            ? 'hover:bg-gray-700/50 text-gray-400 hover:text-gray-300' 
                            : 'hover:bg-gray-100 text-gray-500 hover:text-gray-600'
                        }`}
                        title="Cancel (Esc)"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSaveEdit(comment.id)}
                        className={`p-1.5 rounded-lg transition-all duration-200 hover:scale-110 ${
                          theme === 'dark' 
                            ? 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 hover:text-blue-300' 
                            : 'bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-700'
                        }`}
                        title={`Save (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter)`}
                      >
                        <CheckIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-3">
                    <FileUpload
                      onChange={handleFileUpload}
                      value={editAttachments}
                    />

                    {/* Attachment Preview */}
                    {editAttachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {editAttachments.map((file, index) => (
                          <div>
                            <div key={index} className="flex items-center justify-between">
                              <FilePreview
                                file={file}
                                onRemove={() => {
                                  const currentFiles = editAttachments || [];
                                  const newFiles = [...currentFiles];
                                  newFiles.splice(index, 1);
                                  setEditAttachments(newFiles);
                                }}
                              />
                            </div>
                            <div className="mt-1">
                              
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {isUploading && Object.entries(uploadProgress).map(([fileName, progress]) => (
                      <div key={fileName} className="mt-2">
                        <div className="flex justify-between text-sm">
                          <span className={`truncate max-w-[200px] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {fileName}
                          </span>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {Math.round(progress)}%
                          </span>
                        </div>
                        <div className={`w-full h-1.5 rounded-full overflow-hidden mt-1 ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                          <div
                            className={`h-full ${
                              progress < 100 
                                ? 'bg-blue-500' 
                                : 'bg-green-500'
                            } transition-all duration-300`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {comment.attachments && comment.attachments.length > 0 && (
                    <div className={`mt-3 pt-3 border-t ${
                      theme === 'dark' 
                        ? 'border-gray-700' 
                        : 'border-gray-300'
                    }`}>
                      <div className={`flex items-center mb-2 text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <PaperClipIcon className="w-3 h-3 mr-1" />
                        <span>{comment.attachments.length} attachment{comment.attachments.length > 1 ? 's' : ''}</span>
                      </div>
                      <AttachmentPreview 
                        attachments={comment.attachments} 
                        onDelete={handleDeleteAttachment}
                        showDelete={true}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{comment.content}</p>
                  {comment.attachments && comment.attachments.length > 0 && (
                    <div className={`mt-3 pt-3 border-t ${
                      theme === 'dark' 
                        ? 'border-gray-700' 
                        : 'border-gray-300'
                    }`}>
                      <div className={`flex items-center mb-2 text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <PaperClipIcon className="w-3 h-3 mr-1" />
                        <span>{comment.attachments.length} attachment{comment.attachments.length > 1 ? 's' : ''}</span>
                      </div>
                      <AttachmentPreview 
                        attachments={comment.attachments} 
                        onDelete={async (attachmentId: number) => {
                          console.log('Attachment deleted:', attachmentId);
                          return;
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Metadata - Only shown when not editing */}
              {!isEditing && (
                <div className={`mt-3 pt-3 flex items-center justify-between text-xs border-t ${
                  theme === 'dark' 
                    ? 'text-gray-400 border-gray-700' 
                    : 'text-gray-500 border-gray-300'
                }`}>
                  <div className="flex items-center space-x-2 min-w-0 flex-1 mr-4">
                    <span className={`font-semibold truncate ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {comment.user.full_name}
                    </span>
                    <span className={`w-1 h-1 rounded-full ${
                      theme === 'dark' ? 'bg-gray-600' : 'bg-gray-400'
                    }`}></span>
                    <span className={getRoleBadgeClasses(comment.user.role, theme === 'dark')}>
                      {comment.user.role}
                    </span>
                  </div>
                  <time 
                    dateTime={comment.createdAt} 
                    className={`flex-shrink-0 text-xs whitespace-nowrap font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {formatDateInMyanmarTz(comment.createdAt)}
                  </time>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
